import React, { useState, useEffect } from 'react';
import { useSlideUp, useScaleIn, useStaggerUp, useSlideDown } from './hooks/useAnime';
import { 
  Wallet, TrendingUp, TrendingDown, PieChart as PieIcon, BarChart3, Target, 
  Bell, Sparkles, Users, ShieldCheck, User, LogOut, Plus, Search, 
  Filter, Moon, Sun, CreditCard, ArrowUpRight, ArrowDownRight, Receipt, 
  Mic, Camera, CheckCircle2, XCircle, Trash2, Copy, Clock, Lock, 
  Mail, FileText, Check, DollarSign, Activity, AlertCircle, ChevronRight, Settings
} from 'lucide-react';
import { 
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, 
  PieChart, Pie, Cell, Legend, BarChart, Bar
} from 'recharts';

// Color Palette for Pie Charts
const CATEGORY_COLORS = {
  'Food & Dining': '#6366f1',
  'Transport': '#3b82f6',
  'Shopping': '#ec4899',
  'Education': '#8b5cf6',
  'Healthcare': '#10b981',
  'Rent': '#f59e0b',
  'Bills': '#ef4444',
  'Miscellaneous': '#64748b',
  'Salary': '#10b981',
  'Freelancing': '#06b6d4',
  'Business': '#8b5cf6',
  'Other': '#94a3b8'
};



export default function App() {
    // --- BASIC SYSTEM SETTINGS & SCREEN ROUTING ---
    const [darkMode, setDarkMode] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [authMode, setAuthMode] = useState('login'); 
    const [activeTab, setActiveTab] = useState('dashboard'); 
    
    // --- LIVE DIAGNOSTIC DISPLAY BOX ---
    const [statusMessage, setStatusMessage] = useState('');
    const [statusType, setStatusType] = useState(''); 

    // --- USER PROFILE & AUTH FORM STATES ---
    const [userProfile, setUserProfile] = useState({ name: '', email: '', role: '' });
    const [authForm, setAuthForm] = useState({ name: '', email: '', password: '', role: 'Salaried Employee' });
    const [forgotEmail, setForgotEmail] = useState('');

    // --- DATA ENGINES ---
    const [transactions, setTransactions] = useState([]);
    const [savingsGoals, setSavingsGoals] = useState([]);
    const [reminders, setReminders] = useState([]);
    const [filterCategory, setFilterCategory] = useState('All');
    const [filterType, setFilterType] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [userActivityLogs, setUserActivityLogs] = useState([
      { id: 1, action: 'Initial login session established', time: '10:00 AM' }
    ]);

    const [budgets, setBudgets] = useState({
        'Food & Dining': { allocated: 10000, spent: 0 },
        'Transport': { allocated: 5000, spent: 0 },
        'Shopping': { allocated: 15000, spent: 0 },
        'Education': { allocated: 12000, spent: 0 },
        'Healthcare': { allocated: 8000, spent: 0 },
        'Rent': { allocated: 25000, spent: 0 },
        'Bills': { allocated: 10000, spent: 0 }
    });

    const [showTxModal, setShowTxModal] = useState(false);
    const [newTx, setNewTx] = useState({ type: 'Expense', category: 'Food & Dining', amount: '', date: new Date().toISOString().split('T')[0], desc: '', payment: 'UPI' });
    const [newGoal, setNewGoal] = useState({ name: '', target: '', saved: '', date: '' });
    const [reminderForm, setReminderForm] = useState({ title: '', amount: '', dueDate: '', type: 'Utility Bill' });
    const [splitBill, setSplitBill] = useState({ total: '', people: '', result: '' });
    const [netWorth, setNetWorth] = useState({ bank: '', investments: '', loans: '', calculated: '' });

    const [voiceText, setVoiceText] = useState('');
    const [ocrProgress, setOcrProgress] = useState(0);
    const [ocrStatusText, setOcrStatusText] = useState('');

    const incomeCategories = ['Salary', 'Freelancing', 'Business', 'Other'];
    const expenseCategories = ['Food & Dining', 'Transport', 'Shopping', 'Education', 'Healthcare', 'Rent', 'Bills', 'Miscellaneous'];
    const allCategories = [...expenseCategories, ...incomeCategories];
    const paymentMethods = ['UPI', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Cash'];

    const API_BASE_URL = 'http://localhost:5001/api';

    useEffect(() => {
        const savedUser = localStorage.getItem('activeUser');
        if (savedUser) {
            try {
                const user = JSON.parse(savedUser);
                setUserProfile(user);
                setIsLoggedIn(true);
                const storedTx = localStorage.getItem(`tx_${user.email}`);
                if (storedTx) setTransactions(JSON.parse(storedTx));
                const storedGoals = localStorage.getItem(`goals_${user.email}`);
                if (storedGoals) setSavingsGoals(JSON.parse(storedGoals));
                const storedReminders = localStorage.getItem(`reminders_${user.email}`);
                if (storedReminders) setReminders(JSON.parse(storedReminders));
                const storedLogs = localStorage.getItem(`logs_${user.email}`);
                if (storedLogs) setUserActivityLogs(JSON.parse(storedLogs));
            } catch (e) {
                console.error("Local storage parse error", e);
            }
        }
    }, []);

    // Dark mode HTML class handler
    useEffect(() => {
      if (darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }, [darkMode]);

    const totalIncome = transactions.filter(t => t.type === 'Income').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
    const totalExpense = transactions.filter(t => t.type === 'Expense').reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0);
    const netBalance = totalIncome - totalExpense;
    const healthScore = totalIncome === 0 ? 50 : Math.min(Math.max(Math.round(((totalIncome - totalExpense) / totalIncome) * 100 + 30), 10), 99);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
        return dateStr;
    };

    const saveToLocalStorage = (email, txList, goalsList, remindersList, logsList) => {
        if (!email) return;
        if (txList) localStorage.setItem(`tx_${email}`, JSON.stringify(txList));
        if (goalsList) localStorage.setItem(`goals_${email}`, JSON.stringify(goalsList));
        if (remindersList) localStorage.setItem(`reminders_${email}`, JSON.stringify(remindersList));
        if (logsList) localStorage.setItem(`logs_${email}`, JSON.stringify(logsList));
    };

    useEffect(() => {
        setBudgets(prev => {
            const fresh = { ...prev };
            Object.keys(fresh).forEach(cat => {
                fresh[cat].spent = transactions
                    .filter(t => t.type === 'Expense' && t.category === cat)
                    .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
            });
            return fresh;
        });
    }, [transactions]);

    const searchedFilteredLogs = transactions.filter(t => {
        const matchesCategory = filterCategory === 'All' || t.category === filterCategory;
        const matchesType = filterType === 'All' || t.type === filterType;
        const matchesSearch = t.desc.toLowerCase().includes(searchQuery.toLowerCase()) || 
                              t.category.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesType && matchesSearch;
    });

    // Chart Data Preparation
    const categoryPieData = Object.keys(budgets)
      .map(cat => ({
        name: cat,
        value: budgets[cat].spent
      }))
      .filter(item => item.value > 0);

    const monthlyTrendData = [
      { name: '1-5 Jul', Income: totalIncome * 0.4, Expense: totalExpense * 0.25 },
      { name: '6-10 Jul', Income: totalIncome * 0.1, Expense: totalExpense * 0.20 },
      { name: '11-15 Jul', Income: totalIncome * 0.3, Expense: totalExpense * 0.25 },
      { name: '16-20 Jul', Income: totalIncome * 0.1, Expense: totalExpense * 0.15 },
      { name: '21-26 Jul', Income: totalIncome * 0.1, Expense: totalExpense * 0.15 }
    ];

    const ADMIN = { name: 'Deep', email: 'deepp2276@gmail.com', password: '7069911696', role: 'Admin' };

    // Auth Engine
    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        setStatusMessage('Authenticating...');
        setStatusType('info');

        if (authMode === 'login') {
            if (authForm.email === ADMIN.email && authForm.password === ADMIN.password) {
                const user = { name: ADMIN.name, email: ADMIN.email, role: ADMIN.role };
                localStorage.setItem('activeUser', JSON.stringify(user));
                setUserProfile(user);
                setIsLoggedIn(true);
                setStatusMessage('');
            } else {
                setStatusMessage('⚠️ Invalid email or password.');
                setStatusType('error');
            }
            return;
        }

        setStatusMessage('⚠️ Registration is disabled. Only admin access is allowed.');
        setStatusType('error');
    };

    const handleForgotPasswordSubmit = (e) => {
        e.preventDefault();
        setStatusMessage(`📨 Password recovery link dispatched to ${forgotEmail}!`);
        setStatusType('success');
    };

    const handleSystemLogout = () => {
        localStorage.clear();
        setIsLoggedIn(false);
        setActiveTab('dashboard');
        setStatusMessage('');
    };

    const handleAddTransaction = (e) => {
        e.preventDefault();
        if (!newTx.amount || !newTx.date) return;

        const item = {
            id: Date.now(),
            type: newTx.type,
            category: newTx.category,
            amount: parseFloat(newTx.amount),
            date: newTx.date,
            desc: newTx.desc || `${newTx.category} Transaction`,
            payment: newTx.payment
        };

        const list = [item, ...transactions];
        setTransactions(list);

        const freshLog = { id: Date.now(), action: `Added ${item.type}: ₹${item.amount.toLocaleString('en-IN')}`, time: new Date().toLocaleTimeString() };
        const updatedLogs = [freshLog, ...userActivityLogs];
        setUserActivityLogs(updatedLogs);

        saveToLocalStorage(userProfile.email, list, null, null, updatedLogs);
        setShowTxModal(false);
        setNewTx({ type: 'Expense', category: 'Food & Dining', amount: '', date: new Date().toISOString().split('T')[0], desc: '', payment: 'UPI' });
    };

    const handleDeleteTransaction = (id) => {
      const updated = transactions.filter(t => t.id !== id);
      setTransactions(updated);
      saveToLocalStorage(userProfile.email, updated, null, null, null);
    };

    const handleAddGoal = (e) => {
        e.preventDefault();
        if (!newGoal.name || !newGoal.target) return;
        const item = { id: Date.now(), name: newGoal.name, target: parseFloat(newGoal.target), saved: parseFloat(newGoal.saved || 0), date: newGoal.date || '2026-12-31' };
        const list = [...savingsGoals, item];
        setSavingsGoals(list);
        saveToLocalStorage(userProfile.email, null, list, null, null);
        setNewGoal({ name: '', target: '', saved: '', date: '' });
    };

    const handleUpdateGoalSavings = (id, addedAmount) => {
      const updated = savingsGoals.map(g => g.id === id ? { ...g, saved: Math.min(g.target, g.saved + addedAmount) } : g);
      setSavingsGoals(updated);
      saveToLocalStorage(userProfile.email, null, updated, null, null);
    };

    const handleAddReminder = (e) => {
        e.preventDefault();
        if (!reminderForm.title || !reminderForm.amount) return;
        const item = { id: Date.now(), title: reminderForm.title, amount: parseFloat(reminderForm.amount), dueDate: reminderForm.dueDate || new Date().toISOString().split('T')[0], type: reminderForm.type, status: 'Unpaid' };
        const list = [item, ...reminders];
        setReminders(list);
        saveToLocalStorage(userProfile.email, null, null, list, null);
        setReminderForm({ title: '', amount: '', dueDate: '', type: 'Utility Bill' });
    };

    const toggleReminderStatus = (id) => {
        const updated = reminders.map(r => r.id === id ? { ...r, status: r.status === 'Paid' ? 'Unpaid' : 'Paid' } : r);
        setReminders(updated);
        saveToLocalStorage(userProfile.email, null, null, updated, null);
    };

    const runBillSplitMath = (e) => {
        e.preventDefault();
        const tot = parseFloat(splitBill.total || 0);
        const count = parseInt(splitBill.people || 1, 10);
        setSplitBill({ ...splitBill, result: (tot / (count || 1)).toFixed(2) });
    };

    const runNetWorthMath = (e) => {
        e.preventDefault();
        const b = parseFloat(netWorth.bank || 0);
        const i = parseFloat(netWorth.investments || 0);
        const l = parseFloat(netWorth.loans || 0);
        setNetWorth({ ...netWorth, calculated: (b + i - l).toFixed(2) });
    };

    const handleOcrSimulator = () => {
        setOcrProgress(30);
        setOcrStatusText('Scanning receipt pixels & text...');
        setTimeout(() => {
          setOcrProgress(75);
          setOcrStatusText('Extracting total amount & category...');
        }, 500);
        setTimeout(() => {
            setOcrProgress(100);
            setNewTx({ 
              type: 'Expense', 
              category: 'Food & Dining', 
              amount: '1450', 
              date: new Date().toISOString().split('T')[0], 
              desc: 'Receipt Scan: Italian Bistro Lunch', 
              payment: 'Credit Card' 
            });
            setShowTxModal(true);
            setOcrStatusText('✅ OCR Complete! Form populated with ₹1,450 bill.');
        }, 1100);
    };

    const handleVoiceParserSubmit = (e) => {
        e.preventDefault();
        if (!voiceText) return;
        const raw = voiceText.toLowerCase();
        const match = raw.match(/\d+/);
        const val = match ? parseInt(match[0], 10) : 450;
        
        let cat = 'Miscellaneous';
        if (raw.includes('food') || raw.includes('lunch') || raw.includes('dinner') || raw.includes('coffee')) cat = 'Food & Dining';
        else if (raw.includes('cab') || raw.includes('uber') || raw.includes('petrol') || raw.includes('bus')) cat = 'Transport';
        else if (raw.includes('shopping') || raw.includes('clothes')) cat = 'Shopping';
        else if (raw.includes('rent')) cat = 'Rent';
        else if (raw.includes('bill')) cat = 'Bills';

        const tx = { 
          id: Date.now(), 
          type: 'Expense', 
          category: cat, 
          amount: val, 
          date: new Date().toISOString().split('T')[0], 
          desc: `Voice Entry: "${voiceText}"`, 
          payment: 'UPI' 
        };
        const list = [tx, ...transactions];
        setTransactions(list);
        saveToLocalStorage(userProfile.email, list, null, null, null);
        setVoiceText('');
        setStatusMessage(`🎤 Voice parsed: Recorded ₹${val} under ${cat}!`);
        setStatusType('success');
    };

    const [showSettings, setShowSettings] = useState(false);
    const settingsPanelRef = useScaleIn([showSettings]);

    const loginCardRef = useScaleIn([isLoggedIn]);
    const navRef = useSlideDown([isLoggedIn]);
    const statCardsRef = useStaggerUp([activeTab, transactions.length]);
    const chartSectionRef = useSlideUp([activeTab, transactions.length]);
    const budgetRef = useSlideUp([activeTab, transactions.length]);
    const tableRef = useSlideUp([activeTab, transactions.length]);
    const modalRef = useScaleIn([showTxModal]);
    const tabContentRef = useSlideUp([activeTab]);

    return (
        <div className={`min-h-screen transition-colors duration-200 ${darkMode ? 'dark bg-slate-800 text-slate-100' : 'bg-slate-50/70 text-slate-900'}`}>
            
            {/* --- SCREEN A: LOGIN SUITE GATEWAY --- */}
            {!isLoggedIn && (
                <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-indigo-50/80 via-slate-50 to-purple-50/50 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/40">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl pointer-events-none"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl pointer-events-none"></div>

                    <div ref={loginCardRef} className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl p-8 rounded-3xl shadow-2xl max-w-md w-full border border-slate-200/80 dark:border-slate-800 relative z-10">
                        
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-tr from-indigo-600 to-violet-600 text-white rounded-2xl shadow-lg shadow-indigo-500/30 mb-3">
                                <Wallet className="w-7 h-7" />
                            </div>
                            <h2 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">Smart Expense Tracker</h2>
                            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">Personal Financial Management & AI Analytics</p>
                        </div>

                        {/* Switch Screen Mode Tabs */}
                        <div className="flex bg-slate-100 dark:bg-slate-950 p-1 rounded-2xl mb-5 border border-slate-200/60 dark:border-slate-800">
                            <button type="button" onClick={() => setAuthMode('login')} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${authMode === 'login' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}>Log In</button>
                            <button type="button" onClick={() => setAuthMode('forgot')} className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${authMode === 'forgot' ? 'bg-white dark:bg-slate-800 shadow-sm text-indigo-600 dark:text-indigo-400' : 'text-slate-500 hover:text-slate-800 dark:text-slate-400'}`}>Reset Pass</button>
                        </div>

                        {/* Status Alert Banner */}
                        {statusMessage && (
                            <div className={`p-3.5 mb-5 rounded-2xl text-xs font-semibold flex items-center gap-2 border leading-relaxed ${
                                statusType === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/50 dark:border-rose-900/60 dark:text-rose-300' : 
                                statusType === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/50 dark:border-emerald-900/60 dark:text-emerald-300' : 
                                'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/50 dark:border-indigo-900/60 dark:text-indigo-300'
                            }`}>
                                <AlertCircle className="w-4 h-4 shrink-0" />
                                <span>{statusMessage}</span>
                            </div>
                        )}

                        {authMode !== 'forgot' ? (
                            <form onSubmit={handleAuthSubmit} className="space-y-4">
                                {authMode === 'register' && (
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Full Name</label>
                                        <div className="relative">
                                          <User className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                                          <input type="text" placeholder="Your full name" value={authForm.name} onChange={(e) => setAuthForm({...authForm, name: e.target.value})} className="w-full text-xs pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition" />
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Email Address</label>
                                    <div className="relative">
                                      <Mail className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                                      <input type="email" required placeholder="Enter your email" value={authForm.email} onChange={(e) => setAuthForm({...authForm, email: e.target.value})} className="w-full text-xs pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Password</label>
                                    <div className="relative">
                                      <Lock className="w-4 h-4 absolute left-3.5 top-3.5 text-slate-400" />
                                      <input type="password" required placeholder="••••••••" value={authForm.password} onChange={(e) => setAuthForm({...authForm, password: e.target.value})} className="w-full text-xs pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition" />
                                    </div>
                                </div>
                                {authMode === 'register' && (
                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Occupation / Role</label>
                                        <select value={authForm.role} onChange={(e) => setAuthForm({...authForm, role: e.target.value})} className="w-full text-xs px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition cursor-pointer font-medium">
                                            <option value="Salaried Employee">Salaried Employee</option>
                                            <option value="Student">Student / Academic</option>
                                            <option value="Freelancer / Consultant">Freelancer / Consultant</option>
                                            <option value="Business Owner">Business Owner</option>
                                        </select>
                                    </div>
                                )}
                                <div className="pt-2 space-y-2.5">
                                    <button type="submit" className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white font-bold text-xs rounded-xl shadow-lg shadow-indigo-500/25 transition-all">
                                        {authMode === 'login' ? 'Log In to Workspace' : 'Create Account'}
                                    </button>
                                    

                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wider">Recovery Email</label>
                                    <input type="email" required placeholder="Enter your email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="w-full text-xs px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition" />
                                </div>
                                <div className="space-y-2">
                                    <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow-md transition">
                                        Send Reset Password Link
                                    </button>
                                    <button type="button" onClick={() => setAuthMode('login')} className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 text-xs font-bold rounded-xl hover:bg-slate-200 transition">
                                        Back to Login Screen
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* --- SCREEN B: INSIDE WORKSPACE FRAME (AUTHENTICATED) --- */}
            {isLoggedIn && (
                <div className="min-h-screen flex flex-col">
                    
                    {/* Header Nav Bar */}
                    <header ref={navRef} className="sticky top-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 shadow-xs">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                            
                            {/* App Brand Logo */}
                            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActiveTab('dashboard')}>
                                <div className="w-10 h-10 bg-gradient-to-tr from-indigo-600 to-violet-600 text-white rounded-xl shadow-md shadow-indigo-500/20 flex items-center justify-center font-bold">
                                    <Wallet className="w-5 h-5" />
                                </div>
                                <div>
                                    <span className="text-base font-extrabold tracking-tight bg-gradient-to-r from-indigo-600 via-indigo-500 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent">
                                        SmartExpense
                                    </span>
                                    <span className="block text-[10px] text-slate-400 font-semibold leading-none">Financial Command Center</span>
                                </div>
                            </div>

                            {/* Main Navigation Tabs */}
                            <nav className="hidden lg:flex items-center space-x-1 bg-slate-100/80 dark:bg-slate-950 p-1 rounded-2xl border border-slate-200/60 dark:border-slate-800">
                                {[
                                  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                                  { id: 'goals', label: 'Savings Goals', icon: Target },
                                  { id: 'reminders', label: 'Bills & Reminders', icon: Bell },
                                  { id: 'split', label: 'Tools & Split', icon: Users },
                                  { id: 'admin', label: 'Audit Logs', icon: ShieldCheck },
                                ].map(t => {
                                    const Icon = t.icon;
                                    const isActive = activeTab === t.id;
                                    return (
                                        <button 
                                          key={t.id} 
                                          onClick={() => setActiveTab(t.id)} 
                                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all ${
                                            isActive 
                                              ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm' 
                                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                          }`}
                                        >
                                            <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'}`} />
                                            <span>{t.label}</span>
                                        </button>
                                    );
                                })}
                            </nav>

                            {/* Right Utility Actions */}
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => setDarkMode(!darkMode)}
                                    className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                                    title="Toggle Theme"
                                >
                                    {darkMode ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-slate-600" />}
                                </button>

                                <button
                                    onClick={() => setShowSettings(true)}
                                    className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                                    title="Settings"
                                >
                                    <Settings className="w-4 h-4" />
                                </button>

                                <button 
                                    onClick={() => setShowTxModal(true)}
                                    className="hidden sm:flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white text-xs font-bold rounded-xl shadow-md shadow-indigo-500/20 transition-all transform active:scale-95"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Record Entry</span>
                                </button>

                                <button 
                                    type="button" 
                                    onClick={handleSystemLogout} 
                                    className="p-2 sm:px-3 sm:py-2 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/50 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 text-xs font-bold rounded-xl transition flex items-center gap-1.5"
                                    title="Sign Out"
                                >
                                    <LogOut className="w-4 h-4" />
                                    <span className="hidden sm:inline">Sign Out</span>
                                </button>
                            </div>
                        </div>

                        {/* Mobile Tab Scroll Bar */}
                        <div className="lg:hidden flex overflow-x-auto px-4 py-2 space-x-2 border-t border-slate-200/60 dark:border-slate-800/60 scrollbar-none">
                            {[
                              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                              { id: 'goals', label: 'Goals', icon: Target },
                              { id: 'reminders', label: 'Reminders', icon: Bell },
                              { id: 'split', label: 'Tools', icon: Users },
                              { id: 'admin', label: 'Audit', icon: ShieldCheck },
                            ].map(t => {
                                const Icon = t.icon;
                                const isActive = activeTab === t.id;
                                return (
                                    <button 
                                      key={t.id} 
                                      onClick={() => setActiveTab(t.id)} 
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${
                                        isActive 
                                          ? 'bg-indigo-600 text-white' 
                                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                      }`}
                                    >
                                        <Icon className="w-3.5 h-3.5" />
                                        <span>{t.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </header>

                    {/* Main Workspace Area */}
                    <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 flex-1 w-full space-y-6">
                        
                        {/* Status notification banner if active */}
                        {statusMessage && (
                            <div className={`p-4 rounded-2xl text-xs font-bold flex items-center justify-between border shadow-xs ${
                                statusType === 'error' ? 'bg-rose-50 border-rose-200 text-rose-700 dark:bg-rose-950/50 dark:border-rose-900/60 dark:text-rose-300' : 
                                statusType === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/50 dark:border-emerald-900/60 dark:text-emerald-300' : 
                                'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-950/50 dark:border-indigo-900/60 dark:text-indigo-300'
                            }`}>
                                <div className="flex items-center gap-2">
                                  <Sparkles className="w-4 h-4 shrink-0" />
                                  <span>{statusMessage}</span>
                                </div>
                                <button onClick={() => setStatusMessage('')} className="text-slate-400 hover:text-slate-600 font-bold ml-4">✕</button>
                            </div>
                        )}

                        {/* --- TAB 1: DASHBOARD OVERVIEW --- */}
                        {activeTab === 'dashboard' && (
                            <div className="space-y-6">
                                
                                {/* Section A: Stat Overview Cards */}
                                <section ref={statCardsRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                                    
                                    {/* Total Inflow */}
                                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md transition-all relative overflow-hidden group hover-lift anim-slide-up">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Inflow</span>
                                          <div className="p-2 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 rounded-xl">
                                            <ArrowDownRight className="w-4 h-4" />
                                          </div>
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">₹{totalIncome.toLocaleString('en-IN')}</h3>
                                        <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 mt-2 flex items-center gap-1">
                                          <TrendingUp className="w-3.5 h-3.5" /> Total income recorded
                                        </p>
                                    </div>

                                    {/* Total Outflow */}
                                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md transition-all relative overflow-hidden group hover-lift anim-slide-up anim-delay-1">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Outflow</span>
                                          <div className="p-2 bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 rounded-xl">
                                            <ArrowUpRight className="w-4 h-4" />
                                          </div>
                                        </div>
                                        <h3 className="text-2xl font-black text-slate-900 dark:text-white">₹{totalExpense.toLocaleString('en-IN')}</h3>
                                        <p className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 mt-2 flex items-center gap-1">
                                          <TrendingDown className="w-3.5 h-3.5" /> Total expenses tracked
                                        </p>
                                    </div>

                                    {/* Net Retained Balance */}
                                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md transition-all relative overflow-hidden group hover-lift anim-slide-up anim-delay-2">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Net Retained</span>
                                          <div className="p-2 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded-xl">
                                            <Wallet className="w-4 h-4" />
                                          </div>
                                        </div>
                                        <h3 className={`text-2xl font-black ${netBalance >= 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-rose-600'}`}>
                                          ₹{netBalance.toLocaleString('en-IN')}
                                        </h3>
                                        <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-2">
                                          {netBalance >= 0 ? 'Surplus savings available' : 'Budget deficit alert'}
                                        </p>
                                    </div>

                                    {/* Financial Health Index */}
                                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs hover:shadow-md transition-all relative overflow-hidden group hover-lift anim-slide-up anim-delay-3">
                                        <div className="flex items-center justify-between mb-3">
                                          <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Health Score</span>
                                          <div className="p-2 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded-xl">
                                            <Activity className="w-4 h-4" />
                                          </div>
                                        </div>
                                        <div className="flex items-baseline justify-between">
                                          <h3 className="text-2xl font-black text-amber-600 dark:text-amber-400">{healthScore}<span className="text-xs text-slate-400 font-normal">/100</span></h3>
                                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300">
                                            {healthScore > 75 ? 'Optimal' : healthScore > 50 ? 'Moderate' : 'Needs Review'}
                                          </span>
                                        </div>
                                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full mt-3 overflow-hidden">
                                          <div className="bg-gradient-to-r from-amber-500 to-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${healthScore}%` }} />
                                        </div>
                                    </div>

                                </section>

                                {/* Section B: Analytics & Visual Charts */}
                                <section ref={chartSectionRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    
                                    {/* Income vs Expenses Trend Chart */}
                                    <div className="lg:col-span-2 bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4 anim-slide-up">
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">Cash Flow Analytics</h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Income vs Expenditure timeline distribution</p>
                                          </div>
                                          <div className="flex items-center gap-4 text-xs font-semibold">
                                            <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                                              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Income
                                            </span>
                                            <span className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400">
                                              <span className="w-2.5 h-2.5 rounded-full bg-rose-500"></span> Expenses
                                            </span>
                                          </div>
                                        </div>

                                        <div className="h-64 w-full pt-2">
                                          <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={monthlyTrendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                              <defs>
                                                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                                </linearGradient>
                                                <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                                                  <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3}/>
                                                  <stop offset="95%" stopColor="#f43f5e" stopOpacity={0}/>
                                                </linearGradient>
                                              </defs>
                                              <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} />
                                              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} />
                                              <Tooltip 
                                                contentStyle={{ backgroundColor: darkMode ? '#0f172a' : '#ffffff', borderColor: darkMode ? '#334155' : '#e2e8f0', borderRadius: '12px', fontSize: '12px' }}
                                                formatter={(val) => [`₹${val.toLocaleString('en-IN')}`, '']}
                                              />
                                              <Area type="monotone" dataKey="Income" stroke="#10b981" strokeWidth={2.5} fillOpacity={1} fill="url(#incomeGrad)" />
                                              <Area type="monotone" dataKey="Expense" stroke="#f43f5e" strokeWidth={2.5} fillOpacity={1} fill="url(#expenseGrad)" />
                                            </AreaChart>
                                          </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Category Expenses Breakdown Pie */}
                                    <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4 flex flex-col justify-between anim-slide-up anim-delay-1">
                                        <div>
                                          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Expense Distribution</h3>
                                          <p className="text-xs text-slate-500 dark:text-slate-400">Share by category</p>
                                        </div>

                                        <div className="h-48 w-full">
                                          {categoryPieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                              <PieChart>
                                                <Pie
                                                  data={categoryPieData}
                                                  cx="50%"
                                                  cy="50%"
                                                  innerRadius={50}
                                                  outerRadius={75}
                                                  paddingAngle={4}
                                                  dataKey="value"
                                                >
                                                  {categoryPieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={CATEGORY_COLORS[entry.name] || '#6366f1'} />
                                                  ))}
                                                </Pie>
                                                <Tooltip 
                                                  contentStyle={{ backgroundColor: darkMode ? '#0f172a' : '#ffffff', borderColor: darkMode ? '#334155' : '#e2e8f0', borderRadius: '12px', fontSize: '12px' }}
                                                  formatter={(val) => [`₹${val.toLocaleString('en-IN')}`, 'Spent']}
                                                />
                                              </PieChart>
                                            </ResponsiveContainer>
                                          ) : (
                                            <div className="h-full flex items-center justify-center text-xs text-slate-400">
                                              No category expense data yet
                                            </div>
                                          )}
                                        </div>

                                        <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                                          {categoryPieData.map(item => (
                                            <div key={item.name} className="flex items-center justify-between text-xs">
                                              <div className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[item.name] || '#6366f1' }}></span>
                                                <span className="font-medium text-slate-600 dark:text-slate-300">{item.name}</span>
                                              </div>
                                              <span className="font-bold text-slate-900 dark:text-white">₹{item.value.toLocaleString('en-IN')}</span>
                                            </div>
                                          ))}
                                        </div>
                                    </div>

                                </section>

                                {/* Section C: Category Budget Bars & Transaction Ledger */}
                                <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    
                                    {/* Category Budget Progress */}
                                    <div ref={budgetRef} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                        <div className="flex items-center justify-between">
                                          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Monthly Category Limits</h3>
                                          <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">Budgeted Caps</span>
                                        </div>

                                        <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                                          {Object.keys(budgets).map(k => {
                                            const pct = Math.min(Math.round((budgets[k].spent / budgets[k].allocated) * 100), 100);
                                            const isOver = budgets[k].spent > budgets[k].allocated;
                                            return (
                                              <div key={k} className="space-y-1.5 p-2.5 rounded-xl bg-slate-50/70 dark:bg-slate-950/60 border border-slate-100 dark:border-slate-800">
                                                <div className="flex justify-between items-center text-xs">
                                                  <span className="font-bold text-slate-800 dark:text-slate-200">{k}</span>
                                                  <span className="font-semibold text-slate-500 dark:text-slate-400">
                                                    ₹{budgets[k].spent.toLocaleString('en-IN')} / <span className="text-slate-400">₹{budgets[k].allocated.toLocaleString('en-IN')}</span>
                                                  </span>
                                                </div>
                                                <div className="w-full bg-slate-200/80 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                                                  <div 
                                                    className={`h-full rounded-full anim-bar ${isOver ? 'bg-rose-500' : pct > 80 ? 'bg-amber-500' : 'bg-indigo-600'}`} 
                                                    style={{ width: `${pct}%` }} 
                                                  />
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                    </div>

                                    {/* Transaction Ledger Table */}
                                    <div ref={tableRef} className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs overflow-hidden flex flex-col justify-between">
                                        
                                        {/* Toolbar */}
                                        <div className="p-4 sm:p-5 border-b border-slate-200/80 dark:border-slate-800 space-y-3 bg-slate-50/50 dark:bg-slate-950/40">
                                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                              <Receipt className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Transaction Ledger</h3>
                                            </div>
                                            
                                            <button onClick={() => setShowTxModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition">
                                              <Plus className="w-3.5 h-3.5" />
                                              <span>New Entry</span>
                                            </button>
                                          </div>

                                          {/* Filters & Search */}
                                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            {/* Search input */}
                                            <div className="relative">
                                              <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
                                              <input 
                                                type="text" 
                                                placeholder="Search entries..." 
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full text-xs pl-8 pr-3 py-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 focus:ring-1 focus:ring-indigo-500 outline-none"
                                              />
                                            </div>

                                            {/* Type filter */}
                                            <select 
                                              value={filterType} 
                                              onChange={(e) => setFilterType(e.target.value)}
                                              className="text-xs rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 outline-none font-medium cursor-pointer"
                                            >
                                              <option value="All">All Types (Income/Expense)</option>
                                              <option value="Expense">Expense Only</option>
                                              <option value="Income">Income Only</option>
                                            </select>

                                            {/* Category filter */}
                                            <select 
                                              value={filterCategory} 
                                              onChange={(e) => setFilterCategory(e.target.value)}
                                              className="text-xs rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 outline-none font-medium cursor-pointer"
                                            >
                                              <option value="All">All Categories</option>
                                              {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                          </div>
                                        </div>

                                        {/* Table */}
                                        <div className="overflow-x-auto flex-1">
                                          <table className="w-full text-left border-collapse">
                                            <thead>
                                              <tr className="bg-slate-50 dark:bg-slate-950 text-[11px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200/80 dark:border-slate-800">
                                                <th className="p-3.5 pl-5">Description</th>
                                                <th className="p-3.5">Category</th>
                                                <th className="p-3.5">Date</th>
                                                <th className="p-3.5">Payment</th>
                                                <th className="p-3.5 text-right pr-5">Amount</th>
                                                <th className="p-3.5 text-center">Action</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60 text-xs">
                                              {searchedFilteredLogs.map((t, i) => (
                                                <tr key={t.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition anim-fade" style={{ animationDelay: `${i * 0.04}s` }}>
                                                  <td className="p-3.5 pl-5 font-semibold text-slate-900 dark:text-slate-100">{t.desc}</td>
                                                  <td className="p-3.5">
                                                    <span className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                                      {t.category}
                                                    </span>
                                                  </td>
                                                  <td className="p-3.5 font-mono text-slate-500 dark:text-slate-400">{formatDate(t.date)}</td>
                                                  <td className="p-3.5">
                                                    <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400">
                                                      {t.payment || 'UPI'}
                                                    </span>
                                                  </td>
                                                  <td className={`p-3.5 text-right pr-5 font-black ${t.type === 'Income' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                                    {t.type === 'Income' ? '+' : '-'}₹{t.amount.toLocaleString('en-IN')}
                                                  </td>
                                                  <td className="p-3.5 text-center">
                                                    <button onClick={() => handleDeleteTransaction(t.id)} className="p-1 text-slate-400 hover:text-rose-600 transition" title="Delete Entry">
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                  </td>
                                                </tr>
                                              ))}

                                              {searchedFilteredLogs.length === 0 && (
                                                <tr>
                                                  <td colSpan="6" className="text-center py-12 text-slate-400 text-xs">
                                                    No matching transactions found. Click "New Entry" to add one!
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>

                                        <div className="p-3 border-t border-slate-200/80 dark:border-slate-800 text-[11px] text-slate-400 text-center font-medium">
                                          Showing {searchedFilteredLogs.length} of {transactions.length} total entries
                                        </div>

                                    </div>

                                </section>

                            </div>
                        )}

                        {/* --- TAB 2: SAVINGS GOALS --- */}
                        {activeTab === 'goals' && (
                            <div ref={tabContentRef} className="space-y-6">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800">
                                <div>
                                  <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <Target className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                    Target Savings Goals
                                  </h2>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Track and fund your major financial milestones</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                
                                {/* Goal List */}
                                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {savingsGoals.map(goal => {
                                    const pct = Math.min(Math.round((goal.saved / goal.target) * 100), 100);
                                    return (
                                      <div key={goal.id} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4 flex flex-col justify-between">
                                        <div className="space-y-2">
                                          <div className="flex justify-between items-start">
                                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">{goal.name}</h3>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400">
                                              {pct}% Complete
                                            </span>
                                          </div>
                                          
                                          <div className="flex items-baseline justify-between">
                                            <span className="text-xl font-black text-indigo-600 dark:text-indigo-400">
                                              ₹{goal.saved.toLocaleString('en-IN')}
                                            </span>
                                            <span className="text-xs font-semibold text-slate-400">
                                              Target: ₹{goal.target.toLocaleString('en-IN')}
                                            </span>
                                          </div>

                                          <div className="w-full bg-slate-100 dark:bg-slate-800 h-2.5 rounded-full overflow-hidden">
                                            <div className="bg-gradient-to-r from-indigo-500 to-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                                          </div>
                                        </div>

                                        <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-xs">
                                          <span className="text-slate-400 font-medium">Target Date: {formatDate(goal.date)}</span>
                                          <button 
                                            onClick={() => handleUpdateGoalSavings(goal.id, 1000)}
                                            className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950 dark:hover:bg-indigo-900 text-indigo-600 dark:text-indigo-300 font-bold text-[11px] rounded-lg transition"
                                          >
                                            + Add ₹1,000
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Create Goal Form */}
                                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Create New Goal</h3>
                                  <form onSubmit={handleAddGoal} className="space-y-3">
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Goal Name</label>
                                      <input type="text" required placeholder="e.g. Emergency Fund" value={newGoal.name} onChange={(e) => setNewGoal({...newGoal, name: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Target Amount (₹)</label>
                                      <input type="number" required placeholder="50000" value={newGoal.target} onChange={(e) => setNewGoal({...newGoal, target: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Initial Saved (₹)</label>
                                      <input type="number" placeholder="5000" value={newGoal.saved} onChange={(e) => setNewGoal({...newGoal, saved: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Target Completion Date</label>
                                      <input type="date" required value={newGoal.date} onChange={(e) => setNewGoal({...newGoal, date: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none cursor-pointer" />
                                    </div>
                                    <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow transition">
                                      Add Goal
                                    </button>
                                  </form>
                                </div>

                              </div>
                            </div>
                        )}

                        {/* --- TAB 3: BILL REMINDERS --- */}
                        {activeTab === 'reminders' && (
                            <div ref={tabContentRef} className="space-y-6">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800">
                                <div>
                                  <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <Bell className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                    Bills & Utility Reminders
                                  </h2>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">Never miss upcoming bill payments or subscription renewals</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                
                                {/* Reminders list */}
                                <div className="lg:col-span-2 space-y-3">
                                  {reminders.map(item => (
                                    <div key={item.id} className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center justify-between gap-4">
                                      <div className="flex items-center gap-3">
                                        <button 
                                          onClick={() => toggleReminderStatus(item.id)}
                                          className={`w-6 h-6 rounded-lg border flex items-center justify-center transition ${
                                            item.status === 'Paid' 
                                              ? 'bg-emerald-500 border-emerald-500 text-white' 
                                              : 'border-slate-300 dark:border-slate-700 text-transparent'
                                          }`}
                                        >
                                          <Check className="w-4 h-4" />
                                        </button>
                                        <div>
                                          <h3 className={`text-xs font-bold ${item.status === 'Paid' ? 'line-through text-slate-400' : 'text-slate-900 dark:text-white'}`}>{item.title}</h3>
                                          <span className="text-[11px] text-slate-400 font-medium">Due Date: {formatDate(item.dueDate)} • {item.type}</span>
                                        </div>
                                      </div>

                                      <div className="flex items-center gap-3">
                                        <span className={`text-xs font-black ${item.status === 'Paid' ? 'text-slate-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                          ₹{item.amount.toLocaleString('en-IN')}
                                        </span>
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                          item.status === 'Paid' 
                                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' 
                                            : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                                        }`}>
                                          {item.status}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {/* Add Reminder */}
                                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Schedule Bill Reminder</h3>
                                  <form onSubmit={handleAddReminder} className="space-y-3">
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Bill Title</label>
                                      <input type="text" required placeholder="Broadband Wifi" value={reminderForm.title} onChange={(e) => setReminderForm({...reminderForm, title: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Cost (₹)</label>
                                      <input type="number" required placeholder="999" value={reminderForm.amount} onChange={(e) => setReminderForm({...reminderForm, amount: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>
                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Due Date</label>
                                      <input type="date" required value={reminderForm.dueDate} onChange={(e) => setReminderForm({...reminderForm, dueDate: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none cursor-pointer" />
                                    </div>
                                    <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow transition">
                                      Schedule Reminder
                                    </button>
                                  </form>
                                </div>

                              </div>
                            </div>
                        )}

                        {/* --- TAB 4: AI LAB --- */}
                        {activeTab === 'ailab' && (
                            <div ref={tabContentRef} className="space-y-6">
                              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800">
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                  <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                  AI Finance Lab & OCR Scanner
                                </h2>
                                <p className="text-xs text-slate-500 dark:text-slate-400">Automated receipt scanner & voice command logger</p>
                              </div>

                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                                  {/* OCR Bill Scanner */}
                                  <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                    <div className="flex items-center gap-2">
                                      <Camera className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">Smart OCR Bill Reader</h3>
                                    </div>
                                    <p className="text-xs text-slate-500">Scan paper bills or digital receipts to extract transaction details automatically.</p>
                                    
                                    <div className="border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl p-6 text-center space-y-3 bg-slate-50/50 dark:bg-slate-950/40">
                                      <Receipt className="w-8 h-8 text-slate-400 mx-auto" />
                                      <div className="text-xs text-slate-500">Drop receipt image or click scan button</div>
                                      <button type="button" onClick={handleOcrSimulator} className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl shadow">
                                        Trigger OCR Receipt Scan
                                      </button>
                                    </div>

                                    {ocrProgress > 0 && (
                                      <div className="space-y-1.5">
                                        <div className="w-full bg-slate-100 dark:bg-slate-800 h-2 rounded-full overflow-hidden">
                                          <div className="bg-indigo-600 h-full rounded-full transition-all duration-300" style={{ width: `${ocrProgress}%` }} />
                                        </div>
                                        <p className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 text-center">{ocrStatusText}</p>
                                      </div>
                                    )}
                                  </div>

                                  {/* Voice Input Parser */}
                                  <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                    <div className="flex items-center gap-2">
                                      <Mic className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                      <h3 className="text-sm font-bold text-slate-900 dark:text-white">Voice Command Parsing</h3>
                                    </div>
                                    <p className="text-xs text-slate-500">Type or speak phrases like "Spent 450 on lunch food" to record entries.</p>
                                    
                                    <form onSubmit={handleVoiceParserSubmit} className="space-y-3">
                                      <input 
                                        type="text" 
                                        placeholder="e.g. 'Spent 350 for cab ride'" 
                                        value={voiceText} 
                                        onChange={(e) => setVoiceText(e.target.value)} 
                                        className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" 
                                      />
                                      <button type="submit" className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow">
                                        Parse Voice Command
                                      </button>
                                    </form>
                                  </div>

                              </div>
                            </div>
                        )}

                        {/* --- TAB 5: TOOLS & SPLIT --- */}
                        {activeTab === 'split' && (
                            <div ref={tabContentRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                              
                              {/* Bill Splitter */}
                              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                <div className="flex items-center gap-2">
                                  <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Shared Bill Splitter</h3>
                                </div>
                                <p className="text-xs text-slate-500">Equally divide restaurant bills, group outings, or utility expenses.</p>
                                
                                <form onSubmit={runBillSplitMath} className="space-y-3">
                                  <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Total Amount (₹)</label>
                                    <input type="number" placeholder="3200" value={splitBill.total} onChange={(e) => setSplitBill({...splitBill, total: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Number of People</label>
                                    <input type="number" placeholder="4" value={splitBill.people} onChange={(e) => setSplitBill({...splitBill, people: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-3 bg-white dark:bg-slate-950 outline-none" />
                                  </div>
                                  <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow">Calculate Per Head Share</button>
                                </form>

                                {splitBill.result && (
                                  <div className="p-4 bg-indigo-50 dark:bg-indigo-950/60 rounded-xl text-center border border-indigo-100 dark:border-indigo-900/60 space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Each Person Owes</span>
                                    <div className="text-2xl font-black text-indigo-600 dark:text-indigo-400">₹{splitBill.result}</div>
                                  </div>
                                )}
                              </div>

                              {/* Net Worth Aggregate */}
                              <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-4">
                                <div className="flex items-center gap-2">
                                  <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-white">Net Worth Aggregator</h3>
                                </div>
                                <p className="text-xs text-slate-500">Calculate total net worth across bank accounts, investments, and liabilities.</p>
                                
                                <form onSubmit={runNetWorthMath} className="space-y-3">
                                  <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Bank Assets (₹)</label>
                                    <input type="number" placeholder="240000" value={netWorth.bank} onChange={(e) => setNetWorth({...netWorth, bank: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-2.5 bg-white dark:bg-slate-950 outline-none" />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Mutual Funds & Investments (₹)</label>
                                    <input type="number" placeholder="180000" value={netWorth.investments} onChange={(e) => setNetWorth({...netWorth, investments: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-2.5 bg-white dark:bg-slate-950 outline-none" />
                                  </div>
                                  <div>
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Liabilities & Loans (₹)</label>
                                    <input type="number" placeholder="45000" value={netWorth.loans} onChange={(e) => setNetWorth({...netWorth, loans: e.target.value})} className="w-full text-xs rounded-xl border border-slate-200 dark:border-slate-800 p-2.5 bg-white dark:bg-slate-950 outline-none" />
                                  </div>
                                  <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow">Calculate Net Worth</button>
                                </form>

                                {netWorth.calculated && (
                                  <div className="p-4 bg-emerald-50 dark:bg-emerald-950/60 rounded-xl text-center border border-emerald-100 dark:border-emerald-900/60 space-y-1">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase">Calculated Net Asset Pile</span>
                                    <div className="text-2xl font-black text-emerald-600 dark:text-emerald-400">₹{parseFloat(netWorth.calculated).toLocaleString('en-IN')}</div>
                                  </div>
                                )}
                              </div>

                            </div>
                        )}

                        {/* --- TAB 6: AUDIT LOGS --- */}
                        {activeTab === 'admin' && (
                            <div ref={tabContentRef} className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs max-w-2xl mx-auto space-y-4">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white">System Activity & Audit Logs</h3>
                              </div>

                              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                                {userActivityLogs.map(l => (
                                  <div key={l.id} className="p-3 bg-slate-50 dark:bg-slate-950 text-xs font-mono rounded-xl border border-slate-100 dark:border-slate-800 flex justify-between items-center">
                                    <span className="text-slate-700 dark:text-slate-300 font-medium">{l.action}</span>
                                    <span className="text-slate-400 text-[11px]">{l.time}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                        )}

                    </main>

                    {/* --- SETTINGS PANEL --- */}
                    {showSettings && (
                        <div className="fixed inset-0 z-50 flex justify-end">
                            <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
                            <div ref={settingsPanelRef} className="relative w-full max-w-sm bg-white dark:bg-slate-900 h-full shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col overflow-y-auto">
                                
                                {/* Header */}
                                <div className="flex items-center justify-between p-5 border-b border-slate-200 dark:border-slate-800">
                                    <div className="flex items-center gap-2">
                                        <Settings className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                        <h2 className="text-sm font-bold text-slate-900 dark:text-white">Settings</h2>
                                    </div>
                                    <button onClick={() => setShowSettings(false)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 transition">
                                        ✕
                                    </button>
                                </div>

                                {/* Profile Section */}
                                <div className="p-5 border-b border-slate-200 dark:border-slate-800 space-y-4">
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Profile</p>
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-gradient-to-tr from-indigo-600 to-violet-600 text-white rounded-2xl font-black text-lg flex items-center justify-center shadow-md">
                                            {userProfile.name.charAt(0)}
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-bold text-slate-900 dark:text-white">{userProfile.name}</h3>
                                            <p className="text-xs text-slate-400">{userProfile.role}</p>
                                        </div>
                                    </div>
                                    <div className="space-y-2 text-xs">
                                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                                            <span className="text-slate-400">Email</span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">{userProfile.email}</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
                                            <span className="text-slate-400">Account Type</span>
                                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">{userProfile.role}</span>
                                        </div>
                                        <div className="flex justify-between py-2">
                                            <span className="text-slate-400">Security</span>
                                            <span className="font-semibold text-emerald-600">Encrypted Local</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Sign Out */}
                                <div className="p-5 mt-auto">
                                    <button
                                        onClick={handleSystemLogout}
                                        className="w-full py-2.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/50 dark:hover:bg-rose-900/60 text-rose-600 dark:text-rose-400 font-bold text-xs rounded-xl transition flex items-center justify-center gap-2"
                                    >
                                        <LogOut className="w-4 h-4" />
                                        Sign Out Account
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* --- GLOBAL NEW DIRECT TRANSACTION MODAL --- */}
                    {showTxModal && (
                        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
                            <div ref={modalRef} className="bg-white dark:bg-slate-900 rounded-3xl max-w-md w-full p-6 border border-slate-200 dark:border-slate-800 shadow-2xl space-y-4">
                                <div className="flex justify-between items-center pb-2 border-b border-slate-100 dark:border-slate-800">
                                  <h3 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                    Record Transaction Entry
                                  </h3>
                                  <button type="button" onClick={() => setShowTxModal(false)} className="text-slate-400 hover:text-slate-600 font-bold text-lg">✕</button>
                                </div>

                                <form onSubmit={handleAddTransaction} className="space-y-3">
                                    <div className="grid grid-cols-2 gap-2 bg-slate-100 dark:bg-slate-950 p-1 rounded-xl">
                                        <button 
                                          type="button" 
                                          onClick={() => setNewTx({...newTx, type: 'Expense', category: 'Food & Dining'})} 
                                          className={`py-2 text-xs font-bold rounded-lg transition ${newTx.type === 'Expense' ? 'bg-rose-500 text-white shadow-xs' : 'text-slate-500'}`}
                                        >
                                          Expense
                                        </button>
                                        <button 
                                          type="button" 
                                          onClick={() => setNewTx({...newTx, type: 'Income', category: 'Salary'})} 
                                          className={`py-2 text-xs font-bold rounded-lg transition ${newTx.type === 'Income' ? 'bg-emerald-500 text-white shadow-xs' : 'text-slate-500'}`}
                                        >
                                          Income
                                        </button>
                                    </div>

                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Amount (₹)</label>
                                      <input type="number" step="0.01" required placeholder="0.00" value={newTx.amount} onChange={(e) => setNewTx({...newTx, amount: e.target.value})} className="w-full text-xs font-semibold border border-slate-200 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Category</label>
                                          <select value={newTx.category} onChange={(e) => setNewTx({...newTx, category: e.target.value})} className="w-full text-xs border border-slate-200 dark:border-slate-800 rounded-xl p-2.5 bg-white dark:bg-slate-950 outline-none cursor-pointer font-medium">
                                              {newTx.type === 'Expense' ? expenseCategories.map(c => <option key={c} value={c}>{c}</option>) : incomeCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Payment Method</label>
                                          <select value={newTx.payment} onChange={(e) => setNewTx({...newTx, payment: e.target.value})} className="w-full text-xs border border-slate-200 dark:border-slate-800 rounded-xl p-2.5 bg-white dark:bg-slate-950 outline-none cursor-pointer font-medium">
                                              {paymentMethods.map(p => <option key={p} value={p}>{p}</option>)}
                                          </select>
                                        </div>
                                    </div>

                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Date</label>
                                      <input type="date" required value={newTx.date} onChange={(e) => setNewTx({...newTx, date: e.target.value})} className="w-full text-xs border border-slate-200 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 outline-none cursor-pointer" />
                                    </div>

                                    <div>
                                      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Description / Note</label>
                                      <input type="text" placeholder="e.g. Grocery shopping at D-Mart" value={newTx.desc} onChange={(e) => setNewTx({...newTx, desc: e.target.value})} className="w-full text-xs border border-slate-200 dark:border-slate-800 rounded-xl p-3 bg-white dark:bg-slate-950 outline-none" />
                                    </div>

                                    <button type="submit" className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-md transition">
                                      Save Ledger Entry
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}
